import React, { useCallback, useRef, useState } from 'react';
import LayerControl from './LayerControl';
import SiteStatistics from './SiteStatistics';
import ObjectDetailsPage from './ObjectDetailsPage';
import DraggablePanel from '../ui/DraggablePanel';
import { usePanelSize } from '../../hooks/usePanelSize';
import { resolveDockSideFromDrag, writeUnifiedPanelDock } from '../../Utils/unifiedPanelDock';
import { startPointerDrag } from '../../Utils/pointerDrag';
import MobileSheet from '../ui/MobileSheet';

const PANEL_DEFS = {
  layers: {
    name: 'Layers',
    icon: (
      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M4 6h16M4 12h16M4 18h10" strokeLinecap="round" />
      </svg>
    ),
    component: LayerControl,
  },
  information: {
    name: 'Info',
    icon: (
      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.6">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 10v6M12 7h.01" strokeLinecap="round" />
      </svg>
    ),
    component: ObjectDetailsPage,
  },
  statistics: {
    name: 'Stats',
    icon: (
      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M5 19V9M12 19V5M19 19v-7" strokeLinecap="round" />
      </svg>
    ),
    component: SiteStatistics,
  },
};

function DockIcon({ filled, side = 'left' }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={`w-3.5 h-3.5 ${side === 'right' ? 'scale-x-[-1]' : ''}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      aria-hidden
    >
      <rect x="2" y="2" width="9" height="12" rx="1" fill={filled ? 'currentColor' : 'none'} fillOpacity={filled ? 0.25 : 0} />
      <path d="M11 6h3v4h-3" strokeLinecap="round" strokeLinejoin="round" />
      {filled ? <path d="M5 5h3M5 8h3" strokeLinecap="round" opacity="0.7" /> : null}
    </svg>
  );
}

function DockedPanelShell({
  collapsed,
  width,
  onWidthChange,
  onToggleCollapse,
  activePanel,
  onPanelChange,
  side = 'left',
  children,
}) {
  const resizeRef = useRef(null);
  const dockRight = side === 'right';

  const onResizePointerDown = useCallback(
    (e) => {
      if (e.button != null && e.button !== 0) return;
      const startX = e.clientX;
      const originW = width;
      resizeRef.current = { startX, originW };

      startPointerDrag(e, {
        onMove: (ev) => {
          if (!resizeRef.current) return;
          const maxW = Math.max(260, Math.round(window.innerWidth * 0.55));
          const delta = ev.clientX - resizeRef.current.startX;
          const nextW = Math.min(
            maxW,
            Math.max(260, resizeRef.current.originW + (dockRight ? -delta : delta))
          );
          onWidthChange?.(nextW);
        },
        onEnd: () => {
          resizeRef.current = null;
        },
      });
    },
    [width, onWidthChange, dockRight]
  );

  if (collapsed) {
    return (
      <div
        className={`flex h-full w-10 shrink-0 flex-col bg-zinc-950/95 ${
          dockRight ? 'order-last border-l border-yellow-600/30' : 'border-r border-yellow-600/30'
        }`}
      >
        {Object.entries(PANEL_DEFS).map(([key, panel]) => (
          <button
            key={key}
            type="button"
            title={panel.name}
            onClick={() => {
              onPanelChange(key);
              onToggleCollapse(false);
            }}
            className={`m-0 flex h-10 w-full items-center justify-center border-b border-yellow-600/15 transition-colors ${
              activePanel === key
                ? 'bg-yellow-500/20 text-yellow-400'
                : 'text-zinc-500 hover:bg-yellow-500/10 hover:text-zinc-300'
            }`}
          >
            {panel.icon}
          </button>
        ))}
        <button
          type="button"
          title="Expand panel"
          onClick={() => onToggleCollapse(false)}
          className="m-0 mt-auto flex h-10 w-full items-center justify-center border-t border-yellow-600/20 text-zinc-500 hover:bg-yellow-500/10 hover:text-yellow-400"
        >
          <svg
            viewBox="0 0 16 16"
            className={`w-3.5 h-3.5 ${dockRight ? 'rotate-180' : ''}`}
            fill="currentColor"
            aria-hidden
          >
            <path d="M6 4l4 4-4 4V4z" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div
      className={`relative flex h-full shrink-0 flex-col bg-zinc-950/95 ${
        dockRight
          ? 'order-last border-l border-yellow-600/30 shadow-[-4px_0_24px_rgba(0,0,0,0.25)]'
          : 'border-r border-yellow-600/30 shadow-[4px_0_24px_rgba(0,0,0,0.25)]'
      }`}
      style={{ width }}
    >
      {children}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panel width"
        title="Drag to resize"
        onPointerDown={onResizePointerDown}
        className={`absolute top-0 z-20 h-full w-1.5 cursor-ew-resize touch-none select-none hover:bg-yellow-500/35 ${
          dockRight ? 'left-0' : 'right-0'
        }`}
        style={{ touchAction: 'none' }}
      />
    </div>
  );
}

function PanelChrome({
  activePanel,
  onPanelChange,
  onClose,
  docked,
  dockSide = 'left',
  dockCollapsed,
  onToggleDock,
  onToggleDockCollapse,
  panelWidth,
  onWidthPreset,
  showWidthPresets,
  compact = false,
  children,
}) {
  return (
    <div className={`flex h-full min-h-0 flex-col overflow-hidden bg-zinc-950/95 ${
      compact ? '' : 'rounded-b-md border border-yellow-600/30 border-t-0 backdrop-blur-md'
    }`}>
      <div className={`flex shrink-0 border-b border-yellow-600/25 bg-black/60 ${compact ? 'min-h-[48px]' : ''}`}>
        {Object.entries(PANEL_DEFS).map(([key, panel]) => (
          <button
            key={key}
            type="button"
            onClick={() => onPanelChange(key)}
            className={`m-0 flex flex-1 items-center justify-center gap-1.5 px-2 ${
              compact ? 'py-3 text-[11px]' : 'py-2 text-[10px]'
            } font-semibold uppercase tracking-[0.12em] transition-colors ${
              activePanel === key
                ? 'border-b-2 border-yellow-500 bg-yellow-500/15 text-yellow-400'
                : 'border-b-2 border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
          >
            {panel.icon}
            <span className={compact ? 'inline' : 'hidden sm:inline'}>{panel.name}</span>
          </button>
        ))}
        {!compact && (
        <button
          type="button"
          onClick={onToggleDock}
          title={
            docked
              ? 'Undock — float panel'
              : `Dock to ${dockSide} side — or drag the panel to a screen edge`
          }
          className={`m-0 border-l border-yellow-600/20 px-2 py-2 transition-colors ${
            docked ? 'text-yellow-400 hover:text-yellow-300' : 'text-zinc-500 hover:text-yellow-400'
          }`}
        >
          <DockIcon filled={docked} side={dockSide} />
        </button>
        )}
        {!compact && docked && (
          <button
            type="button"
            onClick={() => onToggleDockCollapse(!dockCollapsed)}
            title={dockCollapsed ? 'Expand panel' : 'Collapse panel'}
            className="m-0 border-l border-yellow-600/20 px-2 py-2 text-zinc-500 hover:text-yellow-400"
          >
            <svg
              viewBox="0 0 16 16"
              className={`h-3 w-3 transition-transform ${
                dockSide === 'right'
                  ? dockCollapsed
                    ? ''
                    : 'rotate-180'
                  : dockCollapsed
                    ? 'rotate-180'
                    : ''
              }`}
              fill="currentColor"
              aria-hidden
            >
              <path d="M10 4L6 8l4 4V4z" />
            </svg>
          </button>
        )}
        {!compact && (
        <button
          type="button"
          onClick={onClose}
          className="m-0 border-l border-yellow-600/20 px-2.5 py-2 text-zinc-500 hover:text-yellow-400"
          title="Close panel"
        >
          <span className="text-sm leading-none">×</span>
        </button>
        )}
      </div>

      {showWidthPresets && (
        <div className="flex shrink-0 border-b border-yellow-600/15 bg-black/40 px-1.5 py-1">
          <div className="flex w-full items-center gap-0.5">
            {[
              { value: 'default', label: 'Auto' },
              { value: '25', label: '25%' },
              { value: '50', label: '50%' },
              { value: '75', label: '75%' },
              { value: '100', label: '100%' },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onWidthPreset(option.value)}
                className={`m-0 flex-1 rounded-sm px-1 py-0.5 text-[9px] font-medium uppercase tracking-wider transition-colors ${
                  panelWidth === option.value
                    ? 'bg-yellow-500 text-black'
                    : 'text-zinc-500 hover:bg-zinc-900 hover:text-zinc-300'
                }`}
                title={`Width ${option.label}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
    </div>
  );
}

const UnifiedPanel = ({
  layoutMode = 'float',
  modelList,
  layerVisibility,
  onToggleVisibility,
  onIsolate,
  onOpacityChange,
  isolatedKeys = [],
  onRefreshCatalogue,
  catalogueLoading,
  activePanel,
  onPanelChange,
  onClose,
  panelWidth,
  onWidthChange,
  projectName,
  recentLayerKeys,
  dockCollapsed,
  dockWidth,
  dockSide = 'left',
  onToggleDock,
  onToggleDockCollapse,
  onDockWidthChange,
  onDockToSide,
  selectedObjectName,
  onOpenStats,
}) => {
  const defaultSize = useCallback(() => {
    const h = typeof window !== 'undefined' ? window.innerHeight : 800;
    return {
      width: 320,
      height: Math.min(720, Math.round(h * 0.85)),
    };
  }, []);

  const { size, setSize } = usePanelSize('unified-panel', defaultSize, {
    minW: 260,
    minH: 220,
  });

  const handleSizeChange = useCallback(
    (next) => {
      setSize(next);
      onWidthChange?.('custom');
    },
    [setSize, onWidthChange]
  );

  const applyWidthPreset = useCallback(
    (value) => {
      onWidthChange?.(value);
      const w = window.innerWidth;
      const h = size.height;
      let width = 320;
      if (value === '25') width = Math.max(260, w * 0.25);
      else if (value === '50') width = Math.max(320, w * 0.5);
      else if (value === '75') width = Math.max(360, w * 0.75);
      else if (value === '100') width = Math.max(320, w - 24);
      else width = 320;
      setSize({ width: Math.round(width), height: h });
    },
    [onWidthChange, size.height, setSize]
  );

  const handleDockWidthChange = useCallback(
    (width) => {
      onDockWidthChange?.(width);
      writeUnifiedPanelDock(projectName, { width });
    },
    [onDockWidthChange, projectName]
  );

  const [dragDockHint, setDragDockHint] = useState(null);

  const handleDragMove = useCallback((pos, ev, meta) => {
    if (!meta?.moved) {
      setDragDockHint(null);
      return;
    }
    setDragDockHint((prev) => {
      const next = resolveDockSideFromDrag({
        clientX: ev?.clientX,
        left: pos?.left,
        originLeft: meta?.originLeft,
      });
      return prev === next ? prev : next;
    });
  }, []);

  const handleDragEnd = useCallback(
    (pos, ev, meta) => {
      const side = meta?.moved
        ? resolveDockSideFromDrag({
            clientX: ev?.clientX,
            left: pos?.left,
            originLeft: meta?.originLeft,
          })
        : null;
      setDragDockHint(null);
      if (side) onDockToSide?.(side);
    },
    [onDockToSide]
  );

  const panelBody = (
    <>
      {activePanel === 'layers' && (
        <LayerControl
          modelList={modelList}
          layerVisibility={layerVisibility}
          onToggleVisibility={onToggleVisibility}
          onIsolate={onIsolate}
          onOpacityChange={onOpacityChange}
          isolatedKeys={isolatedKeys}
          onRefresh={onRefreshCatalogue}
          refreshing={catalogueLoading}
          projectName={projectName}
          recentLayerKeys={recentLayerKeys}
        />
      )}
      {activePanel === 'statistics' && <SiteStatistics stopeID={selectedObjectName} />}
      {activePanel === 'information' && <ObjectDetailsPage onOpenStats={onOpenStats} />}
    </>
  );

  const chrome = (
    <PanelChrome
      activePanel={activePanel}
      onPanelChange={onPanelChange}
      onClose={onClose}
      docked={layoutMode === 'dock'}
      dockSide={dockSide}
      dockCollapsed={dockCollapsed}
      onToggleDock={onToggleDock}
      onToggleDockCollapse={onToggleDockCollapse}
      panelWidth={panelWidth}
      onWidthPreset={applyWidthPreset}
      showWidthPresets={layoutMode === 'float'}
      compact={layoutMode === 'sheet'}
    >
      {panelBody}
    </PanelChrome>
  );

  if (layoutMode === 'sheet') {
    const sheetTitle = PANEL_DEFS[activePanel]?.name || 'Layers';
    return (
      <MobileSheet title={sheetTitle} onClose={onClose} height="min(72dvh, 36rem)" zIndex={1280}>
        {chrome}
      </MobileSheet>
    );
  }

  if (layoutMode === 'dock') {
    return (
      <DockedPanelShell
        collapsed={dockCollapsed}
        width={dockWidth}
        onWidthChange={handleDockWidthChange}
        onToggleCollapse={onToggleDockCollapse}
        activePanel={activePanel}
        onPanelChange={onPanelChange}
        side={dockSide}
      >
        {chrome}
      </DockedPanelShell>
    );
  }

  return (
    <>
      <DraggablePanel
        id="unified-panel"
        handleLabel="Panel"
        handleTitle="Drag to move — drag to a screen edge to dock"
        zIndex={1210}
        resizable
        size={size}
        onSizeChange={handleSizeChange}
        minWidth={260}
        minHeight={220}
        className="pointer-events-auto shadow-[0_16px_48px_rgba(0,0,0,0.55)]"
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
      >
        {chrome}
      </DraggablePanel>
      {dragDockHint ? (
        <div
          className="pointer-events-none fixed inset-y-0 z-[3000] w-1.5 bg-yellow-400/80 shadow-[0_0_16px_rgba(234,179,8,0.7)]"
          style={dragDockHint === 'right' ? { right: 0 } : { left: 0 }}
          aria-hidden
        />
      ) : null}
    </>
  );
};

export default UnifiedPanel;
